import { useState } from 'react';

import { ExternalLink } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TldrawUiDialogCloseButton, TldrawUiDialogBody, TldrawUiDialogTitle } from 'tldraw';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/new-button';
import Select from '@/components/atoms/Select';
import { Textarea } from '@/components/atoms/textarea';
import { MyOptionType } from '@/types';

interface NodeCreationModalProps {
  onClose: () => void;
  onSave: (nodeData: { dateText: string; description: string; documentNames: string; docId: string }) => void;
}

export const NodeCreationModal = ({ onClose, onSave }: NodeCreationModalProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const caseId = searchParams.get('caseId');

  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [selectedDoc, setSelectedDoc] = useState<MyOptionType>(null);

  const { data: allDocuments } = useGetDocIds(caseId);

  const handleSave = () => {
    if (description.trim()) {
      if (!selectedDoc?.value || !selectedDoc?.label) {
        return;
      }

      const documentNames = selectedDoc.label;
      onSave({
        dateText: date.trim(),
        description: description.trim(),
        documentNames: documentNames,
        docId: selectedDoc.value,
      });
      // Reset form
      setDate('');
      setDescription('');
      setSelectedDoc(null);
    }
  };

  const handleClose = () => {
    setDate('');
    setDescription('');
    setSelectedDoc(null);
    onClose();
  };

  const handleNavigateToFacts = () => {
    trackCustomEvent(TrackingEvents.AddFactToChronologyOnVisualChronologyPage, {
      caseId: caseId,
    });
    navigate(`/app/chronos/case-editor/data/facts?caseId=${caseId}`);
  };

  const documentOptions =
    allDocuments?.documents?.filter(Boolean).map((doc) => ({
      label: doc.file.name,
      value: doc.id,
    })) ?? [];

  return (
    <>
      <div className="border-b border-gray-200 px-7 py-2">
        <div className="flex items-center justify-between">
          <TldrawUiDialogTitle>
            <p className="text-base font-semibold text-gray-900">Add New Chronology Event</p>
          </TldrawUiDialogTitle>
          <TldrawUiDialogCloseButton />
        </div>
      </div>

      <TldrawUiDialogBody className="w-[450px]">
        <div className="px-4 py-1">
          <div className="flex flex-col gap-4">
            <button
              onClick={handleNavigateToFacts}
              className="group flex w-full cursor-pointer items-center justify-between rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-blue-600 transition-all duration-200 hover:border-blue-200 hover:bg-blue-100"
            >
              <p className="text-xs text-blue-500">Add a fact to this chronology from the facts table</p>

              <ExternalLink
                size={14}
                className="cursor-pointer transition-transform duration-200 group-hover:scale-110"
                onClick={handleNavigateToFacts}
              />
            </button>

            <div className="flex flex-col gap-2">
              <Label htmlFor="date">Date</Label>
              <Input
                type="text"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                placeholder="e.g., January 15, 2023"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="document">Document</Label>
              <Select
                options={documentOptions}
                selectedOption={selectedDoc}
                onChange={(newValue) => setSelectedDoc(newValue)}
                placeholder={'Select a document'}
                className="w-full rounded-md bg-white outline-none"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter event description..."
                rows={3}
                className="resize-none"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-between gap-3">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!date.trim() || !selectedDoc?.value || !description.trim()}
            >
              Create Event
            </Button>
          </div>
        </div>
      </TldrawUiDialogBody>
    </>
  );
};
